import { useMemo } from 'react';
import { format } from 'date-fns';
import type { Collect } from '../types';

export interface MonthlyStats {
  totalWorkedMinutes: number;
  workingDaysCount: number;
  estimatedBankMinutes: number;
}

const EXPECTED_DAILY_MINUTES = 8 * 60;

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function useMonthlyStats(collects: Collect[], month?: string): MonthlyStats {
  return useMemo(() => {
    const currentMonth = month ?? format(new Date(), 'yyyy-MM');
    const monthCollects = collects.filter((c) => c.date.startsWith(currentMonth));

    const byDate = new Map<string, Collect[]>();
    for (const c of monthCollects) {
      const list = byDate.get(c.date) ?? [];
      list.push(c);
      byDate.set(c.date, list);
    }

    let totalWorkedMinutes = 0;
    for (const dayCollects of byDate.values()) {
      const sorted = [...dayCollects].sort((a, b) => a.time.localeCompare(b.time));
      for (let i = 0; i + 1 < sorted.length; i += 2) {
        const entry = sorted[i];
        const exit = sorted[i + 1];
        if (entry && exit) {
          const diff = timeToMinutes(exit.time) - timeToMinutes(entry.time);
          if (diff > 0) totalWorkedMinutes += diff;
        }
      }
    }

    const workingDaysCount = byDate.size;
    const expectedTotal = workingDaysCount * EXPECTED_DAILY_MINUTES;
    const estimatedBankMinutes = totalWorkedMinutes - expectedTotal;

    return { totalWorkedMinutes, workingDaysCount, estimatedBankMinutes };
  }, [collects, month]);
}

export function formatHoursMinutes(minutes: number): string {
  const absMin = Math.abs(minutes);
  const h = Math.floor(absMin / 60);
  const m = absMin % 60;
  const sign = minutes < 0 ? '-' : '+';
  const formatted = m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
  return minutes === 0 ? `${h}h` : `${sign} ${formatted}`;
}
